import { useEffect, useRef } from 'react';

import { useMemoizeObject, useUserPrint } from '@app/hooks';
import {
  addWsEventListener,
  createWSConnection,
  subscribeToWsReconnect,
} from '@app/utils';
import {
  invokeWSMethod,
  unregisterWSMethod,
} from '@app/utils/createWSConnection';

// Интервал страховочного поллинга, пока в истории есть исходящие pending:
// при потерянном WS-событии оператор увидит смену статуса не позднее чем
// через ~минуту (30 с интервал + время запроса).
const PENDING_STATUS_POLL_INTERVAL_MS = 30_000;

interface UseLeadMessageHistoryEffectsParams {
  selectedLeadClientIds: number[];
  hasPendingOutgoingMessages: boolean;
  onClientHistoryUpdateHandler: (data: { jsonData: string }) => void;
  refreshLatestHistory: () => Promise<void>;
}

export const useLeadMessageHistoryEffects = ({
  selectedLeadClientIds,
  hasPendingOutgoingMessages,
  onClientHistoryUpdateHandler,
  refreshLatestHistory,
}: UseLeadMessageHistoryEffectsParams) => {
  const { database } = useUserPrint();
  const memoClientIds = useMemoizeObject(selectedLeadClientIds);
  const onClientHistoryUpdateHandlerRef = useRef(onClientHistoryUpdateHandler);
  const refreshLatestHistoryRef = useRef(refreshLatestHistory);

  useEffect(() => {
    onClientHistoryUpdateHandlerRef.current = onClientHistoryUpdateHandler;
  }, [onClientHistoryUpdateHandler]);

  useEffect(() => {
    refreshLatestHistoryRef.current = refreshLatestHistory;
  }, [refreshLatestHistory]);

  useEffect(() => {
    if (!database) return undefined;
    if (!memoClientIds?.length) return undefined;

    void createWSConnection(async () => {
      for (const clientId of memoClientIds) {
        await invokeWSMethod('ListenClient', database, clientId);
      }
    });

    // Снимаем вызовы с реестра восстановления, чтобы реконнект не перевызывал
    // ListenClient по всем лидам, открывавшимся за сессию.
    return () => {
      for (const clientId of memoClientIds) {
        unregisterWSMethod('ListenClient', database, clientId);
      }
    };
  }, [memoClientIds, database]);

  // addWsEventListener вместо subscribeToWsEvent: у события может быть
  // несколько живых подписчиков (карточка лида + чат, встроенный в карточку
  // клиента), и отписка снимает только СВОЙ колбэк, не задевая соседей.
  useEffect(() => {
    const stableHistoryUpdateHandler = (data: { jsonData: string }) => {
      onClientHistoryUpdateHandlerRef.current(data);
    };
    let isCancelled = false;
    let unsubscribe: (() => void) | undefined;

    void createWSConnection(() => {
      if (isCancelled) return;

      unsubscribe = addWsEventListener(
        'OnClientHistoryUpdate',
        stableHistoryUpdateHandler
      );
    });

    return () => {
      isCancelled = true;
      unsubscribe?.();
    };
  }, []);

  // Пока соединения не было, сервер мог отправить OnClientHistoryUpdate —
  // после переподключения дозапрашиваем свежую историю (последние сутки).
  useEffect(() => {
    const unsubscribe = subscribeToWsReconnect(() => {
      void refreshLatestHistoryRef.current();
    });

    return unsubscribe;
  }, []);

  // Страховочный поллинг: пока есть исходящие сообщения в статусе pending,
  // периодически дозапрашиваем свежую историю. Покрывает потерю WS-события
  // о смене статуса и «глухое» соединение (сервер не шлёт события живому
  // сокету с просроченным токеном) — иначе «часики» залипают до перезагрузки.
  useEffect(() => {
    if (!hasPendingOutgoingMessages) return undefined;

    const intervalId = window.setInterval(() => {
      void refreshLatestHistoryRef.current();
    }, PENDING_STATUS_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasPendingOutgoingMessages]);
};
